import {Trans} from '@lingui/macro';
import {capitalize} from 'lodash';
import React, {ReactNode, useState} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUnmountCancelToken} from '../../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {
  ApiCustomField,
  CustomFieldBaseModel,
  CustomFieldErrorCodesEnum,
} from '../../../models/api/customFieldModel';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {postApiAsync} from '../../../services/http/http';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {getCustomFieldsBaseUrl, parseApiError} from '../customFieldsHelpers';
import {CustomFieldForm} from './customFieldForm';

/*
 * Types.
 */

interface NewCustomFieldFormProps {
  tabPage: SettingsCustomFieldsPagesEnum;
  setSuccessBannerMessage: HandlerOf<ReactNode>;
  setErrorBannerMessage: HandlerOf<ReactNode>;
}

/*
 * Component.
 */

export const NewCustomFieldForm = ({
  tabPage,
  setSuccessBannerMessage,
  setErrorBannerMessage,
}: NewCustomFieldFormProps) => {
  const companyUrl = useAppSelector(getCompanyUrl);
  const [errors, setErrors] = useState({});
  const cancelToken = useUnmountCancelToken();
  const {navigateToLink} = useLinkNavigation();

  const onSubmit = async (data: CustomFieldBaseModel) => {
    setErrors({});
    try {
      await postApiAsync(ApiCustomField, `${companyUrl}/${tabPage}/custom_fields`, data, {
        cancelToken,
      });
      setSuccessBannerMessage(
        <Trans id="custom-fields-create-success">
          Created &quot;{capitalize(data.name)}&quot; custom field
        </Trans>,
      );
      navigateToLink(`${getCustomFieldsBaseUrl(tabPage)}/overview`);
    } catch (err) {
      if (err.body.code === CustomFieldErrorCodesEnum.CUSTOM_FIELD_ALREADY_EXIST) {
        setErrors({name: <Trans id="custom-fields-new-conflict-error">This name already exists</Trans>});
      } else {
        setErrorBannerMessage(parseApiError(err.body || err));
      }

      throw err;
    }
  };

  return <CustomFieldForm tabPage={tabPage} onSubmit={onSubmit} errors={errors} setErrors={setErrors} />;
};
